import { createSelector } from 'reselect';
import type { State } from '../interface';
import type { StatementValue } from '../interface';
import { getCompanyId } from './utils';
import type { SelectedStatement } from '@/pages/CompanyReport/redux/interface';
import {
  RiskProps,
  SummaryRiskStatus,
} from '@/pages/CompanyReport/components/SummaryCompare';
import {
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_INFORMATION_ANCHOR,
  LegacyAnchor,
  FUTURE_EARNINGS,
  PAST_EARNINGS_REVENUE,
  HEALTH_DEBT_HISTORY,
  HEALTH_CASH_RUNWAY,
  DIVIDEND_YIELD,
  DIVIDEND_PAYOUT_CURRENT,
} from '@/constants/anchors';
import { slugify } from '@/utilities/slugify';

type Severity = 'minor' | 'major' | 'none';

export interface StatementSelectorProps {
  companyId?: string;
}

export const getStatement = (state, ownProps: StatementSelectorProps) => {
  const company = state.company as State;
  if (ownProps && ownProps.companyId) {
    return company.statements[ownProps.companyId];
  }
  const currentCompanyId = getCompanyId(state, {});
  if (currentCompanyId) {
    return company.statements[currentCompanyId];
  }
};

export const defaultStatement = (): SelectedStatement => ({
  title: '',
  condensedTitle: '',
  description: '',
  formattedDescription: '',
  value: 'UnInitialised',
  context: {},
  severity: 'none',
  question: '',
  outcomeName: '',
  url: null,
  _tooltip_: [],
});

export const statementOrDefault =
  (statement: State['statements'][''] | undefined) =>
  (prop: string): SelectedStatement => {
    if (statement) {
      const current = statement[prop];
      if (current) {
        return {
          title: current.title,
          condensedTitle: slugify(current.title),
          description: current.description,
          formattedDescription: current.description.replace(
            /\[\[(.*?)\]\]/g,
            (matched, original) => {
              return original;
            }
          ),
          context: current.context,
          value: current.value,
          severity: current.severity,
          question: current.question,
          outcomeName: current.outcomeName,
          url: current.url,
          _tooltip_: current._tooltip_,
        };
      }
    }
    return defaultStatement();
  };

export interface RewardSummaryStatements {
  isGoodValue: SelectedStatement;
  isCompanyGrowing: SelectedStatement;
  hasCompanyBeenGrowing: SelectedStatement;
  isDividendAttractive: SelectedStatement;
}

export interface RiskSummaryStatements {
  isAbleToAchieveProfitability: SelectedStatement;
  areRevenueAndEarningsExpectedToGrow: SelectedStatement;
  isInAGoodFinancialPosition: SelectedStatement;
  isDividendSustainable: SelectedStatement;
  hasStableSharePriceOverPast3Months: SelectedStatement;
  hasSufficientFinancialData: SelectedStatement;
  hasHighQualityEarnings: SelectedStatement;
  hasNetProfitMarginImprovedOverPastYear: SelectedStatement;
  hasNoConcerningRecentEvents: SelectedStatement;
  hasNoSubstantialInsiderSellingOverPastQuarter: SelectedStatement;
  hasNotDilutedOverPastYear: SelectedStatement;
  hasMeaningfulRevenue: SelectedStatement;
  hasMeaningfulMarketCap: SelectedStatement;
  hasNoNegativeEquity: SelectedStatement;
}
const defaultRewardSummaryStatements = (): RewardSummaryStatements => ({
  isGoodValue: defaultStatement(),
  isCompanyGrowing: defaultStatement(),
  hasCompanyBeenGrowing: defaultStatement(),
  isDividendAttractive: defaultStatement(),
});
const defaultRiskSummaryStatements = (): RiskSummaryStatements => ({
  isAbleToAchieveProfitability: defaultStatement(),
  areRevenueAndEarningsExpectedToGrow: defaultStatement(),
  isInAGoodFinancialPosition: defaultStatement(),
  isDividendSustainable: defaultStatement(),
  hasStableSharePriceOverPast3Months: defaultStatement(),
  hasSufficientFinancialData: defaultStatement(),
  hasHighQualityEarnings: defaultStatement(),
  hasNetProfitMarginImprovedOverPastYear: defaultStatement(),
  hasNoConcerningRecentEvents: defaultStatement(),
  hasNoSubstantialInsiderSellingOverPastQuarter: defaultStatement(),
  hasNotDilutedOverPastYear: defaultStatement(),
  hasMeaningfulRevenue: defaultStatement(),
  hasMeaningfulMarketCap: defaultStatement(),
  hasNoNegativeEquity: defaultStatement(),
});

interface RewardData {
  rewardSummaryStatements: RewardSummaryStatements;
  hasRewards: boolean;
}

export const getRewardSummaryStatements = createSelector(
  [getStatement],
  (statement): RewardData => {
    if (statement) {
      const getStatement = statementOrDefault(statement);
      const isGoodValue = getStatement('IsGoodValue');
      const isCompanyGrowing = getStatement('IsGrowingProfitOrRevenue');
      const hasCompanyBeenGrowing = getStatement(
        'HasBeenGrowingProfitOrRevenue'
      );
      const isDividendAttractive = getStatement('IsDividendAttractive');
      return {
        rewardSummaryStatements: {
          isGoodValue,
          isCompanyGrowing,
          hasCompanyBeenGrowing,
          isDividendAttractive,
        },
        hasRewards:
          isGoodValue.value === 'Pass' ||
          isCompanyGrowing.value === 'Pass' ||
          hasCompanyBeenGrowing.value === 'Pass' ||
          isDividendAttractive.value === 'Pass' ||
          false,
      };
    }
    return {
      rewardSummaryStatements: defaultRewardSummaryStatements(),
      hasRewards: false,
    };
  }
);

const getRiskSummaryStatements = createSelector(
  [getStatement],
  (statement): RiskSummaryStatements => {
    if (statement) {
      const getStatement = statementOrDefault(statement);

      return {
        isAbleToAchieveProfitability: getStatement(
          'IsAbleToAchieveProfitability'
        ),
        areRevenueAndEarningsExpectedToGrow: getStatement(
          'AreRevenueAndEarningsExpectedToGrow'
        ),
        isInAGoodFinancialPosition: getStatement('IsInAGoodFinancialPosition'),
        isDividendSustainable: getStatement('IsDividendSustainable'),
        hasStableSharePriceOverPast3Months: getStatement(
          'HasStableSharePriceOverPast3Months'
        ),
        hasHighQualityEarnings: getStatement('HasHighQualityEarnings'),
        hasNetProfitMarginImprovedOverPastYear: getStatement(
          'HasNetProfitMarginImprovedOverPastYear'
        ),
        hasSufficientFinancialData: getStatement('HasSufficientFinancialData'),
        hasNoConcerningRecentEvents: getStatement(
          'HasNoConcerningRecentEvents'
        ),
        hasNoSubstantialInsiderSellingOverPastQuarter: getStatement(
          'HasNoSubstantialInsiderSellingOverPastQuarter'
        ),
        hasNotDilutedOverPastYear: getStatement('HasNotDilutedOverPastYear'),
        hasMeaningfulRevenue: getStatement('HasMeaningfulRevenue'),
        hasMeaningfulMarketCap: getStatement('HasMeaningfulMarketCap'),
        hasNoNegativeEquity: getStatement('HasNoNegativeEquity'),
      };
    }
    return defaultRiskSummaryStatements();
  }
);

const getFilteredRisks = (
  riskSummaryStatements: RiskSummaryStatements,
  riskValue: StatementValue,
  riskSeverity: Severity
): (SelectedStatement & RiskProps)[] => {
  return Object.keys(riskSummaryStatements).reduce((riskArr, riskName) => {
    const { value, severity, description, outcomeName } =
      riskSummaryStatements[riskName];
    const { hash, sectionName } = getRiskSectionData(riskName, outcomeName);
    if (
      value === riskValue &&
      severity === riskSeverity &&
      description !== ''
    ) {
      return riskArr.concat({
        ...riskSummaryStatements[riskName],
        name: riskName,
        hash: '#' + hash,
        sectionName,
      });
    } else return riskArr;
  }, []);
};

interface FailingRiskData {
  firstFourFailingRisks: (SelectedStatement & RiskProps)[];
  allRisks: (SelectedStatement & RiskProps)[];
  totalFailingRisks: number;
}

export const getFailingRisksData = createSelector(
  [getRiskSummaryStatements],
  (riskSummaryStatements): FailingRiskData => {
    const failingRisks = [
      ...getFilteredRisks(riskSummaryStatements, 'Fail', 'major'),
      ...getFilteredRisks(riskSummaryStatements, 'Fail', 'minor'),
    ];

    const passingRisks = getFilteredRisks(
      riskSummaryStatements,
      'Pass',
      'none'
    );
    const noDataRisks = getFilteredRisks(
      riskSummaryStatements,
      'No data',
      'none'
    );
    const allRisks = [...failingRisks, ...passingRisks, ...noDataRisks];
    return {
      firstFourFailingRisks: failingRisks.slice(0, 4).map((risk) => ({
        ...risk,
        description: risk.description.replace(/\[\[/g, '').replace(/\]\]/g, ''),
      })),
      allRisks,
      totalFailingRisks: failingRisks.length,
    };
  }
);

interface RiskSectionData {
  hash: string;
  sectionName: string;
}

const getHashIsAbleToAchieveProfitability = (
  outcomeName: string
): RiskSectionData => {
  switch (outcomeName) {
    case 'OUTCOME_TRUE_BECOME_NEXT':
    case 'OUTCOME_FALSE_BECOME_NEXT':
      return {
        hash: FUTURE_EARNINGS,
        sectionName: 'Section 4.1',
      };
    case 'OUTCOME_TRUE':
    case 'OUTCOME_TRUE_LONG_TERM':
    case 'OUTCOME_TRUE_REDUCE_LOSS':
    case 'OUTCOME_FALSE_REDUCE_LOSS':
      return {
        hash: PAST_EARNINGS_REVENUE,
        sectionName: 'Section 5.1',
      };
    default:
      return { hash: COMPANY_FUTURE_ANCHOR, sectionName: 'Section 4.0' };
  }
};

const getHashShareRevenueAndEarningsExpectedToGrow = (
  outcomeName: string
): RiskSectionData => {
  switch (outcomeName) {
    case 'OUTCOME_TRUE_FUTURE':
    case 'OUTCOME_FALSE_MAJOR_RISK_FUTURE':
      return {
        hash: FUTURE_EARNINGS,
        sectionName: 'Section 4.1',
      };
    case 'OUTCOME_TRUE_PAST':
    case 'OUTCOME_FALSE_MAJOR_RISK_PAST':
      return {
        hash: PAST_EARNINGS_REVENUE,
        sectionName: 'Section 5.1',
      };
    default:
      return { hash: COMPANY_FUTURE_ANCHOR, sectionName: 'Section 4.0' };
  }
};

const getHashIsInAGoodFinancialPosition = (
  outcomeName: string
): RiskSectionData => {
  switch (outcomeName) {
    case 'OUTCOME_TRUE_NO_DEBT':
    case 'OUTCOME_TRUE_LOW_DEBT':
    case 'OUTCOME_FALSE_INTEREST':
    case 'OUTCOME_FALSE_CASH_DEBT':
    case 'OUTCOME_FALSE_HIGH_DEBT':
      return {
        hash: HEALTH_DEBT_HISTORY,
        sectionName: 'Section 6.2',
      };
    case 'OUTCOME_TRUE_CASH_RUNWAY':
    case 'OUTCOME_FALSE_CASH_RUNWAY_MAJOR':
    case 'OUTCOME_FALSE_CASH_RUNWAY_MINOR':
      return {
        hash: HEALTH_CASH_RUNWAY,
        sectionName: 'Section 6.4',
      };
    default:
      return { hash: COMPANY_HEALTH_ANCHOR, sectionName: 'Section 6.0' };
  }
};

const getHashIsDividendSustainable = (outcomeName: string): RiskSectionData => {
  switch (outcomeName) {
    case 'OUTCOME_TRUE':
    case 'OUTCOME_FALSE_UNSTABLE':
      return {
        hash: DIVIDEND_YIELD,
        sectionName: 'Section 7.2',
      };
    case 'OUTCOME_FALSE_MAJOR_RISK':
    case 'OUTCOME_FALSE_MINOR_RISK':
    case 'OUTCOME_FALSE_MINOR_RISK_EARNINGS':
    case 'OUTCOME_FALSE_MINOR_RISK_CASH_FLOW':
      return {
        hash: DIVIDEND_PAYOUT_CURRENT,
        sectionName: 'Section 7.3',
      };
    default:
      return { hash: COMPANY_DIVIDEND_ANCHOR, sectionName: 'Section 7.0' };
  }
};

const getHashHasSufficientFinancialData = (
  outcomeName: string
): RiskSectionData => {
  if (outcomeName === 'OUTCOME_TRUE_ANALYSTS') {
    return { hash: COMPANY_FUTURE_ANCHOR, sectionName: 'Section 4.0' };
  }
  return { hash: COMPANY_PAST_ANCHOR, sectionName: 'Section 5.0' };
};

const getRiskSectionData = (
  statementName: string,
  outcomeName: string
): RiskSectionData => {
  switch (statementName) {
    case 'isAbleToAchieveProfitability':
      return getHashIsAbleToAchieveProfitability(outcomeName);
    case 'areRevenueAndEarningsExpectedToGrow':
      return getHashShareRevenueAndEarningsExpectedToGrow(outcomeName);
    case 'isInAGoodFinancialPosition':
      return getHashIsInAGoodFinancialPosition(outcomeName);
    case 'isDividendSustainable':
      return getHashIsDividendSustainable(outcomeName);
    case 'hasStableSharePriceOverPast3Months':
    case 'hasNoConcerningRecentEvents':
      return {
        hash: LegacyAnchor.HEADER_PRICE_EVENTS,
        sectionName: 'Section 2.1',
      };
    case 'hasSufficientFinancialData':
      return getHashHasSufficientFinancialData(outcomeName);
    case 'hasHighQualityEarnings':
    case 'hasNetProfitMarginImprovedOverPastYear':
    case 'hasMeaningfulRevenue':
      return {
        hash: PAST_EARNINGS_REVENUE,
        sectionName: 'Section 5.1',
      };
    case 'hasNoSubstantialInsiderSellingOverPastQuarter':
      return {
        hash: LegacyAnchor.OWNERSHIP_INSIDER_TRADING,
        sectionName: 'Section 9.1',
      };
    case 'hasNotDilutedOverPastYear': {
      return {
        hash: LegacyAnchor.OWNERSHIP,
        sectionName: 'Section 9.3',
      };
    }
    case 'hasMeaningfulMarketCap':
      return { hash: COMPANY_INFORMATION_ANCHOR, sectionName: 'Section 10.0' };
    case 'hasNoNegativeEquity':
      return { hash: COMPANY_HEALTH_ANCHOR, sectionName: 'Section 6.2' };
    default:
      return { hash: '', sectionName: '' };
  }
};

export const getRiskSummaryStatus = createSelector(
  [getRiskSummaryStatements],
  (riskSummaryStatements): SummaryRiskStatus => {
    const riskList: SelectedStatement[] = Object.values(riskSummaryStatements);

    const insufficientData =
      riskList.length === 0 ||
      riskList.filter((risk) => risk.value === 'Fail' || risk.value === 'Pass')
        .length === 0;

    const hasPassedAllRisks =
      riskList.filter((risk) => risk.value === 'Pass').length ===
      riskList.length;

    const hasNotFailedAnyRisk =
      riskList.filter((risk) => risk.value === 'Fail').length === 0;

    if (insufficientData) return SummaryRiskStatus.InsufficientData;
    else if (hasPassedAllRisks) return SummaryRiskStatus.HasPassedAllRisks;
    else if (hasNotFailedAnyRisk) return SummaryRiskStatus.HasNotFailedAnyRisk;
    else return SummaryRiskStatus.HasFailedRisks;
  }
);

export interface ValueStatements {
  isUndervaluedBasedOnDCF: SelectedStatement;
  isHighlyUndervaluedBasedOnDCF: SelectedStatement;
  isUndervaluedOnPERelativeToPeers: SelectedStatement;
  isUndervaluedOnPERelativeToMarket: SelectedStatement;
  isUndervaluedBasedOnPEG: SelectedStatement;
  isUndervaluedBasedOnPB: SelectedStatement;
  isDCFMeaningless: SelectedStatement;
  isGoodValueComparingPreferredMultipleToPeersAverageValue: SelectedStatement;
  isGoodValueComparingPreferredMultipleToIndustry: SelectedStatement;
  hasPreferredMultiple: SelectedStatement;
  hasSecondaryMultiple: SelectedStatement;
  hasTertiaryMultiple: SelectedStatement;
  isGoodValueComparingRatioToFairRatio: SelectedStatement;
  isAnalystForecastTrustworthy: SelectedStatement;
}

const defaultValueStatement = () => ({
  isUndervaluedBasedOnDCF: defaultStatement(),
  isHighlyUndervaluedBasedOnDCF: defaultStatement(),
  isUndervaluedOnPERelativeToPeers: defaultStatement(),
  isUndervaluedOnPERelativeToMarket: defaultStatement(),
  isUndervaluedBasedOnPEG: defaultStatement(),
  isUndervaluedBasedOnPB: defaultStatement(),
  isDCFMeaningless: defaultStatement(),
  isGoodValueComparingPreferredMultipleToPeersAverageValue: defaultStatement(),
  isGoodValueComparingPreferredMultipleToIndustry: defaultStatement(),
  hasPreferredMultiple: defaultStatement(),
  hasSecondaryMultiple: defaultStatement(),
  hasTertiaryMultiple: defaultStatement(),
  isGoodValueComparingRatioToFairRatio: defaultStatement(),
  isAnalystForecastTrustworthy: defaultStatement(),
});

export const getValueStatements = createSelector(
  [getStatement],
  (statement): ValueStatements => {
    if (statement) {
      const getStatement = statementOrDefault(statement);
      return {
        isUndervaluedBasedOnDCF: getStatement('IsUndervaluedBasedOnDCF'),
        isHighlyUndervaluedBasedOnDCF: getStatement(
          'IsHighlyUndervaluedBasedOnDCF'
        ),
        isUndervaluedOnPERelativeToPeers: getStatement(
          'IsUndervaluedOnPERelativeToPeers'
        ),
        isUndervaluedOnPERelativeToMarket: getStatement(
          'IsUndervaluedOnPERelativeToMarket'
        ),
        isUndervaluedBasedOnPEG: getStatement('IsUndervaluedBasedOnPEG'),
        isUndervaluedBasedOnPB: getStatement('IsUndervaluedBasedOnPB'),
        isDCFMeaningless: getStatement('IsDCFMeaningless'),
        isGoodValueComparingPreferredMultipleToPeersAverageValue: getStatement(
          'IsGoodValueComparingPreferredMultipleToPeersAverageValue'
        ),
        isGoodValueComparingPreferredMultipleToIndustry: getStatement(
          'IsGoodValueComparingPreferredMultipleToIndustry'
        ),
        hasPreferredMultiple: getStatement('HasPreferredMultiple'),
        hasSecondaryMultiple: getStatement('HasSecondaryMultiple'),
        hasTertiaryMultiple: getStatement('HasTertiaryMultiple'),
        isGoodValueComparingRatioToFairRatio: getStatement(
          'IsGoodValueComparingRatioToFairRatio'
        ),
        isAnalystForecastTrustworthy: getStatement(
          'IsAnalystForecastTrustworthy'
        ),
      };
    }
    return defaultValueStatement();
  }
);

export interface FutureStatements {
  isExpectedProfitGrowthAboveRiskFreeRate: SelectedStatement;
  isExpectedAnnualProfitGrowthAboveMarket: SelectedStatement;
  isExpectedAnnualProfitGrowthHigh: SelectedStatement;
  isExpectedRevenueGrowthAboveMarket: SelectedStatement;
  isExpectedRevenueGrowthHigh: SelectedStatement;
  isReturnOnEquityForecastAboveBenchmark: SelectedStatement;
  isFutureAnalysisMeaningless: SelectedStatement;
}

const defaultFutureStatement = (): FutureStatements => ({
  isExpectedProfitGrowthAboveRiskFreeRate: defaultStatement(),
  isExpectedAnnualProfitGrowthAboveMarket: defaultStatement(),
  isExpectedAnnualProfitGrowthHigh: defaultStatement(),
  isExpectedRevenueGrowthAboveMarket: defaultStatement(),
  isExpectedRevenueGrowthHigh: defaultStatement(),
  isReturnOnEquityForecastAboveBenchmark: defaultStatement(),
  isFutureAnalysisMeaningless: defaultStatement(),
});

export const getFutureStatements = createSelector(
  [getStatement],
  (statement): FutureStatements => {
    if (statement) {
      const getStatement = statementOrDefault(statement);
      return {
        isExpectedProfitGrowthAboveRiskFreeRate: getStatement(
          'IsExpectedProfitGrowthAboveRiskFreeRate'
        ),
        isExpectedAnnualProfitGrowthAboveMarket: getStatement(
          'IsExpectedAnnualProfitGrowthAboveMarket'
        ),
        isExpectedAnnualProfitGrowthHigh: getStatement(
          'IsExpectedAnnualProfitGrowthHigh'
        ),
        isExpectedRevenueGrowthAboveMarket: getStatement(
          'IsExpectedRevenueGrowthAboveMarket'
        ),
        isExpectedRevenueGrowthHigh: getStatement(
          'IsExpectedRevenueGrowthHigh'
        ),
        isReturnOnEquityForecastAboveBenchmark: getStatement(
          'IsReturnOnEquityForecastAboveBenchmark'
        ),
        isFutureAnalysisMeaningless: getStatement(
          'IsFutureAnalysisMeaningless'
        ),
      };
    }
    return defaultFutureStatement();
  }
);

export interface PastStatements {
  hasGrownProfitsOverPast5Years: SelectedStatement;
  hasProfitGrowthAccelerated: SelectedStatement;
  isGrowingFasterThanIndustry: SelectedStatement;
  isReturnOnEquityAboveThreshold: SelectedStatement;
  isPastAnalysisMeaningless: SelectedStatement;
  hasHighQualityPastEarnings: SelectedStatement;
  hasPastNetProfitMarginImprovedOverLastYear: SelectedStatement;
}

const defaultPastStatement = (): PastStatements => ({
  hasGrownProfitsOverPast5Years: defaultStatement(),
  hasProfitGrowthAccelerated: defaultStatement(),
  isGrowingFasterThanIndustry: defaultStatement(),
  isReturnOnEquityAboveThreshold: defaultStatement(),
  isPastAnalysisMeaningless: defaultStatement(),
  hasHighQualityPastEarnings: defaultStatement(),
  hasPastNetProfitMarginImprovedOverLastYear: defaultStatement(),
});

export const getPastStatements = createSelector(
  [getStatement],
  (statement): PastStatements => {
    if (statement) {
      const getStatement = statementOrDefault(statement);
      return {
        hasGrownProfitsOverPast5Years: getStatement(
          'HasGrownProfitsOverPast5Years'
        ),
        hasProfitGrowthAccelerated: getStatement('HasProfitGrowthAccelerated'),
        isGrowingFasterThanIndustry: getStatement(
          'IsGrowingFasterThanIndustry'
        ),
        isReturnOnEquityAboveThreshold: getStatement(
          'IsReturnOnEquityAboveThreshold'
        ),
        isPastAnalysisMeaningless: getStatement('IsPastAnalysisMeaningless'),
        hasHighQualityPastEarnings: getStatement('HasHighQualityPastEarnings'),
        hasPastNetProfitMarginImprovedOverLastYear: getStatement(
          'HasPastNetProfitMarginImprovedOverLastYear'
        ),
      };
    }
    return defaultPastStatement();
  }
);

export interface HealthStatements {
  areShortTermLiabilitiesCovered: SelectedStatement;
  areLongTermLiabilitiesCovered: SelectedStatement;
  isDebtLevelAppropriate: SelectedStatement;
  hasDebtReducedOverTime: SelectedStatement;
  isDebtCoveredByCashflow: SelectedStatement;
  isInterestCoveredByProfit: SelectedStatement;
  hasCashRunwayIfStable: SelectedStatement;
  hasCashRunwayIfGrowing: SelectedStatement;
  hasAnAppropriateLevelOfAssets: SelectedStatement;
  hasAppropriateBadLoanAllowance: SelectedStatement;
  hasPrimarilyLowRiskFunding: SelectedStatement;
  hasAppropriateLoanLevel: SelectedStatement;
  hasPrimarilyDepositFunding: SelectedStatement;
  hasAppropriateNonPerformingLoans: SelectedStatement;
  isHealthAnalysisMeaningless: SelectedStatement;
  isBank: SelectedStatement;
  isProfitable: SelectedStatement;
  isProfitableLongTerm: SelectedStatement;
}

const getDefaultHealthStatements = (): HealthStatements => ({
  areShortTermLiabilitiesCovered: defaultStatement(),
  areLongTermLiabilitiesCovered: defaultStatement(),
  isDebtLevelAppropriate: defaultStatement(),
  hasDebtReducedOverTime: defaultStatement(),
  isDebtCoveredByCashflow: defaultStatement(),
  isInterestCoveredByProfit: defaultStatement(),
  hasCashRunwayIfStable: defaultStatement(),
  hasCashRunwayIfGrowing: defaultStatement(),
  hasAnAppropriateLevelOfAssets: defaultStatement(),
  hasAppropriateBadLoanAllowance: defaultStatement(),
  hasPrimarilyLowRiskFunding: defaultStatement(),
  hasAppropriateLoanLevel: defaultStatement(),
  hasPrimarilyDepositFunding: defaultStatement(),
  hasAppropriateNonPerformingLoans: defaultStatement(),
  isHealthAnalysisMeaningless: defaultStatement(),
  isBank: defaultStatement(),
  isProfitable: defaultStatement(),
  isProfitableLongTerm: defaultStatement(),
});

export const getHealthStatements = createSelector(
  [getStatement],
  (statement): HealthStatements => {
    if (statement) {
      const getStatement = statementOrDefault(statement);
      return {
        areShortTermLiabilitiesCovered: getStatement(
          'AreShortTermLiabilitiesCovered'
        ),
        areLongTermLiabilitiesCovered: getStatement(
          'AreLongTermLiabilitiesCovered'
        ),
        isDebtLevelAppropriate: getStatement('IsDebtLevelAppropriate'),
        hasDebtReducedOverTime: getStatement('HasDebtReducedOverTime'),
        isDebtCoveredByCashflow: getStatement('IsDebtCoveredByCashflow'),
        isInterestCoveredByProfit: getStatement('IsInterestCoveredByProfit'),
        hasCashRunwayIfStable: getStatement('HasCashRunwayIfStable'),
        hasCashRunwayIfGrowing: getStatement('HasCashRunwayIfGrowing'),
        hasAnAppropriateLevelOfAssets: getStatement(
          'HasAnAppropriateLevelOfAssets'
        ),
        hasAppropriateBadLoanAllowance: getStatement(
          'HasAppropriateBadLoanAllowance'
        ),
        hasPrimarilyLowRiskFunding: getStatement('HasPrimarilyLowRiskFunding'),
        hasAppropriateLoanLevel: getStatement('HasAppropriateLoanLevel'),
        hasPrimarilyDepositFunding: getStatement('HasPrimarilyDepositFunding'),
        hasAppropriateNonPerformingLoans: getStatement(
          'HasAppropriateNonPerformingLoans'
        ),
        isHealthAnalysisMeaningless: getStatement(
          'IsHealthAnalysisMeaningless'
        ),
        isBank: getStatement('IsBank'),
        isProfitable: getStatement('IsProfitable'),
        isProfitableLongTerm: getStatement('IsProfitableLongTerm'),
      };
    }
    return getDefaultHealthStatements();
  }
);

export interface DividendStatements {
  isDividendSignificant: SelectedStatement;
  isDividendYieldTopTier: SelectedStatement;
  isDividendStable: SelectedStatement;
  isDividendGrowing: SelectedStatement;
  isDividendCovered: SelectedStatement;
  isDividendCoveredIn3Years: SelectedStatement;
  isDividendCoveredByFreeCashFlow: SelectedStatement;
  isBank: SelectedStatement;
}

const getDefaultDividendStatements = (): DividendStatements => ({
  isDividendSignificant: defaultStatement(),
  isDividendYieldTopTier: defaultStatement(),
  isDividendStable: defaultStatement(),
  isDividendGrowing: defaultStatement(),
  isDividendCovered: defaultStatement(),
  isDividendCoveredIn3Years: defaultStatement(),
  isDividendCoveredByFreeCashFlow: defaultStatement(),
  isBank: defaultStatement(),
});

export const getDividendStatements = createSelector(
  [getStatement],
  (statement): DividendStatements => {
    if (statement) {
      const getStatement = statementOrDefault(statement);
      return {
        isDividendSignificant: getStatement('IsDividendSignificant'),
        isDividendYieldTopTier: getStatement('IsDividendYieldTopTier'),
        isDividendStable: getStatement('IsDividendStable'),
        isDividendGrowing: getStatement('IsDividendGrowing'),
        isDividendCovered: getStatement('IsDividendCovered'),
        isDividendCoveredIn3Years: getStatement('IsDividendCoveredIn3Years'),
        isDividendCoveredByFreeCashFlow: getStatement(
          'IsDividendCoveredByFreeCashFlow'
        ),
        isBank: getStatement('IsBank'),
      };
    }
    return getDefaultDividendStatements();
  }
);

export interface ManagementStatements {
  isCEOCompensationAppropriate: SelectedStatement;
  isCEOCompensationChangeJustified: SelectedStatement;
  isManagementTeamSeasoned: SelectedStatement;
  isBoardSeasoned: SelectedStatement;
  hasNetInsiderBuyingOver3Months: SelectedStatement;
  isNotDilutedOverPastYear: SelectedStatement;
}

const getDefaultManagementStatement = (): ManagementStatements => ({
  isCEOCompensationAppropriate: defaultStatement(),
  isCEOCompensationChangeJustified: defaultStatement(),
  isManagementTeamSeasoned: defaultStatement(),
  isBoardSeasoned: defaultStatement(),
  hasNetInsiderBuyingOver3Months: defaultStatement(),
  isNotDilutedOverPastYear: defaultStatement(),
});

export const getManagementStatements = createSelector(
  [getStatement],
  (statement): ManagementStatements => {
    if (!statement) return getDefaultManagementStatement();
    const getStatement = statementOrDefault(statement);
    return {
      isCEOCompensationAppropriate: getStatement(
        'IsCEOCompensationAppropriate'
      ),
      isCEOCompensationChangeJustified: getStatement(
        'IsCEOCompensationChangeJustified'
      ),
      isManagementTeamSeasoned: getStatement('IsManagementTeamSeasoned'),
      isBoardSeasoned: getStatement('IsBoardSeasoned'),
      hasNetInsiderBuyingOver3Months: getStatement(
        'HasNetInsiderBuyingOver3Months'
      ),
      isNotDilutedOverPastYear: getStatement('IsNotDilutedOverPastYear'),
    };
  }
);
